import { type ISODate, daysBetween, parseISODate, toISODate } from './dates';

export const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Normaliza para comparação: sem acento, minúsculas. */
export const normalize = (s: unknown) =>
  String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const WEEKDAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export const WEEKDAYS_LONG = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

export const fmtDate = (s: ISODate | Date, o: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('pt-BR', o).format(typeof s === 'string' ? parseISODate(s) : s);

/** "12 mar" */
export const fmtShort = (s: ISODate) => `${parseISODate(s).getDate()} ${fmtDate(s, { month: 'short' }).replace('.', '')}`;

/** hoje, amanhã, ontem, dia da semana (até 6 dias) ou data curta. */
export function fmtRelativeDay(s: ISODate, today: ISODate) {
  const n = daysBetween(today, s);
  if (n === 0) return 'hoje';
  if (n === 1) return 'amanhã';
  if (n === -1) return 'ontem';
  if (n > 1 && n < 7) return fmtDate(s, { weekday: 'long' });
  return fmtShort(s);
}

/** "agora", "há 5 min", "há 3 h", "ontem", "há 4 dias". */
export function fmtAgo(iso: string, now = Date.now()) {
  const s = (now - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'agora';
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / 86400);
  return d === 1 ? 'ontem' : `há ${d} dias`;
}

/** Carimbo da lista de notas: hora (hoje), Ontem, dia da semana ou dd/mm/aaaa. */
export function fmtNoteStamp(iso: string, today: ISODate) {
  const d = new Date(iso);
  const n = daysBetween(today, toISODate(d));
  const p = (x: number) => String(x).padStart(2, '0');
  if (n === 0) return `${p(d.getHours())}:${p(d.getMinutes())}`;
  if (n === -1) return 'Ontem';
  if (n > -7) return capitalize(fmtDate(d, { weekday: 'long' }));
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export const greeting = (hour: number) => (hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite');

const nf1 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** R$ abreviado: "R$ 1,2 bi", "R$ 3,4 mi", "R$ 500,0 mil". */
export function fmtMoneyShort(v: unknown) {
  if (typeof v !== 'number') return '';
  const a = Math.abs(v);
  const body = a >= 1e9 ? nf1.format(a / 1e9) + ' bi' : a >= 1e6 ? nf1.format(a / 1e6) + ' mi' : nf1.format(a / 1e3) + ' mil';
  return (v < 0 ? '−' : '') + 'R$ ' + body;
}
export const fmtMoney = (v: unknown) => (typeof v !== 'number' ? '' : (v < 0 ? '−' : '') + nf2.format(Math.abs(v)));
export const fmtPercent = (x: number) => (!Number.isFinite(x) ? '' : (x < 0 ? '−' : '') + nf1.format(Math.abs(x) * 100) + '%');
export const fmtMillions = (v: number) => nf1.format(v / 1e6);
export const truncate = (s: unknown, max: number) => {
  const t = String(s ?? '');
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
};

/** Escapa texto para inserir em HTML (dicas do mapa). */
export const escapeHtml = (s: unknown) =>
  String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
